import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Clock, ArrowLeft, User, ExternalLink } from "lucide-react";
import { getSupabaseClient } from "@/lib/env";
import { CustomPackage } from "@/types/packages";
import { Loader2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import ErrorDisplay from "@/components/ErrorDisplay";

const CustomPackageDetail = () => {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const [pkg, setPkg] = useState<CustomPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sellerData, setSellerData] = useState<any | null>(null);

  useEffect(() => {
    let isMounted = true;
    
    async function loadPackageDetails() {
      try {
        setLoading(true);
        setError(null);
        
        const packageIdOrSlug = idOrSlug;
        if (!packageIdOrSlug) {
          throw new Error("No package ID or slug provided");
        }
        
        const supabase = await getSupabaseClient();
        
        // Check if the id parameter is a UUID (ID) or a string (slug)
        const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(packageIdOrSlug);
        console.log(`Looking up package by ${isUuid ? 'ID' : 'slug'}: ${packageIdOrSlug}`);
        
        // Fetch the custom package with its features - either by ID or by slug
        const query = supabase
          .from("custom_packages")
          .select("*, features:package_features(*), seller_profile:seller_profiles(*)")
        
        // If it's a UUID, search by ID, otherwise search by slug
        const { data: packageData, error: packageError } = await (
          isUuid 
            ? query.eq("id", packageIdOrSlug).single()
            : query.eq("slug", packageIdOrSlug).single()
        );
        
        if (packageError) {
          console.error("Error fetching package:", packageError);
          throw packageError;
        }
        if (!packageData) {
          console.error("Package not found");
          throw new Error("Package not found");
        }
        
        console.log("Package found:", packageData.title);
        
        // Fetch seller data
        const { data: sellerData, error: sellerError } = await supabase
          .from("seller_profiles")
          .select("*")
          .eq("id", packageData.seller_id)
          .single();
        
        if (sellerError) throw sellerError;
        
        if (isMounted) {
          setPkg(packageData as CustomPackage);
          setSellerData(sellerData);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error in CustomPackageDetail:", err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : "Unknown error occurred");
          setLoading(false);
        }
      }
    }

    loadPackageDetails();

    return () => {
      isMounted = false;
    };
  }, [idOrSlug]);

  if (loading) {
    return (
      <div className="container mx-auto py-12 px-4">
        <div className="flex justify-center items-center min-h-[400px]">
          <Loader2 className="h-10 w-10 animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !pkg) {
    return (
      <ErrorDisplay 
        title="Couldn't load package details" 
        message={error || "Package not found"}
        action={
          <div className="flex flex-col gap-3">
            <p className="text-sm text-gray-600 mb-2">This could be because:</p>
            <ul className="text-sm text-left list-disc ml-5 mb-4">
              <li>The package no longer exists</li>
              <li>The URL might be incorrect</li>
              <li>There might be a temporary connection issue</li>
            </ul>
            <Link to="/custom-packages">
              <Button variant="default" className="w-full">View All Packages</Button>
            </Link>
          </div>
        }
      />
    );
  }
  
  // Sort features by sort_order
  const sortedFeatures = [...pkg.features].sort((a, b) => a.sort_order - b.sort_order);
  
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(pkg.price_cents / 100);

  return (
    <>
      <Helmet>
        <title>{pkg.title} | BackLink Hub</title>
      </Helmet>
      
      <div className="container mx-auto py-8 px-4">
        <div className="mb-6">
          <Link to="/custom-packages" className="flex items-center text-brand-blue hover:underline mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to all packages
          </Link>
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold">{pkg.title}</h1>
              <p className="text-gray-600 mt-1">{pkg.description}</p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Badge variant={pkg.is_active ? "default" : "secondary"}>
                {pkg.is_active ? "Active" : "Inactive"}
              </Badge>
              
              <span className="flex items-center text-gray-500">
                <Clock className="w-4 h-4 mr-1" /> 
                {pkg.estimated_turnaround_days} days delivery
              </span>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <Card>
              <CardContent className="pt-6">
                <h2 className="text-xl font-semibold mb-4">Package Features</h2>
                <div className="space-y-4">
                  {sortedFeatures.map((feature) => (
                    <div key={feature.id} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-medium">{feature.title}</h3>
                        {feature.description && (
                          <p className="text-gray-600">{feature.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="text-3xl font-bold mb-4">{formattedPrice}</div>
                <Button className="w-full mb-3">Purchase Package</Button>
                <Button variant="outline" className="w-full">Contact Seller</Button>
                
                <Separator className="my-6" />
                
                {sellerData && (
                  <div>
                    <h3 className="text-lg font-medium mb-3">About the Seller</h3>
                    <Link to={`/sellers/${sellerData.slug || sellerData.id}`} className="flex items-center gap-3 hover:bg-gray-50 p-2 rounded-md">
                      <Avatar>
                        <AvatarImage src={sellerData.logo_url || ''} alt={sellerData.name} />
                        <AvatarFallback>{sellerData.name.substring(0, 2).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">{sellerData.name}</div>
                        <div className="text-sm text-gray-500">View Profile</div>
                      </div>
                      <ExternalLink className="w-4 h-4 ml-auto text-gray-400" />
                    </Link>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomPackageDetail; 